import {
	Component,
	ElementRef,
	viewChild,
	ChangeDetectionStrategy,
	input,
	TemplateRef,
	computed,
	effect,
	DestroyRef,
	inject,
	untracked,
	signal,
} from '@angular/core';
import { NgTemplateOutlet, NgComponentOutlet } from '@angular/common';
import { IconName } from '@skylab-assets/icons';
import { match } from 'ts-pattern';
import { BaseNotificationComponentDataConfig, BaseNotificationItemConfig, BaseNotificationTemplateDataConfig, BasePopoverComponent } from './base-popover';
import { NotificationStackOptions, NotificationStackService } from './notification/notification-stack.service';
import { ButtonComponent } from '../button/button.component';
import { ToastComponent } from './toast.component';
import { IconComponent } from '../icon/icon.component';

interface ActionsButton {
	text: string;
	icon?: IconName;
	isPrimary?: boolean;
	action: (event: Event) => void;
}

export interface ToastConfig extends BaseNotificationItemConfig {
	/**
	 * Toast type (color)
	 */
	type?: 'error' | 'warning' | 'info' | 'success' | 'pending';

	/**
	 * Whether to show the close button
	 * Default: false
	 */
	closeButton?: boolean;

	/**
	 * Action buttons to show in the toast
	 */
	actions?: ActionsButton[];

	/**
	 * Whether to pause auto-close on hover
	 * Default: true
	 */
	pauseAutoCloseOnHover?: boolean;
}

@Component({
	selector: 'skl-toast-popover',
	template: `
		@let data = options().data;

		<skl-toast
			#toast
			class="toast"
			[type]="toastType()"
		>
			<section>

				@if (icon(); as iconName) {
					<div class="toast-icon-frame">
						<skl-icon class="toast-icon" [name]="iconName" />
					</div>
				}

				<div class="toast-content">

					@if (isTemplateData(data)) {
						<ng-container *ngTemplateOutlet="data.content; context: { $implicit: data, data: data }" />
					} @else {
						@if (title()) {
							<header>{{ title() }}</header>
						}

						@if (isComponentData(data)) {
							<ng-container *ngComponentOutlet="data.content; inputs: data.contentProps" />
						} @else {
							<article>{{ data.content }}</article>
						}
					}

				</div>

				<div class="toast-actions">

					@for (action of actions(); track $index) {
						<skl-button
							[typeName]="getActionTypeName(action)"
							(click)="action.action($event)"
						>
							@if (action.icon) {
								<skl-icon [name]="action.icon" />
							}
							{{ action.text }}
						</skl-button>
					}

				</div>
			</section>

			@if (closeButton()) {
				<skl-button
					class="toast-close"
					typeName="transparent"
					data-kind="icon-only"
					[disabled]="disableClose()"
					(click)="close()"
				>
					<skl-icon name="cross" />
				</skl-button>
			}

			@if (showProgress()) {
				<span
					slot="end"
					class="progress active"
					[style.animation-duration]="duration() + 'ms'"
					[style.animation-play-state]="isPaused() ? 'paused' : 'running'"
				></span>
			}
		</skl-toast>
	`,
	host: {
		'(mouseenter)': 'handleMouseEnter()',
		'(mouseleave)': 'handleMouseLeave()',
		'[attr.data-type]': 'toastType()',
	},
	styles: `
		:host {
			--_toast-color: var(--text-minor-color);

			&[data-type="success"] { 
				--_toast-color: var(--success-color); 
			}

			&[data-type="warning"] { 
				--_toast-color: var(--warning-color); 
			}

			&[data-type="error"] { 
				--_toast-color: var(--error-color); 
			}

			&[data-type="info"], 
			&[data-type="pending"] { 
				--_toast-color: var(--info-color); 
			}

			.toast-content {
				display: flex;
				flex-wrap: wrap;
				flex-direction: column;
				gap: .25em;
				text-box: trim-both cap alphabetic;
				flex: 1;

				header {
					font-weight: 600;
					text-wrap: balance;
					color: var(--text-color);
				}

				article {
					font-size: .9rem;
					text-wrap: pretty;
					color: var(--text-minor-color);
				}
			}

			.toast-icon-frame {
				color: var(--_toast-color);
				display: grid;
				place-items: center;
				aspect-ratio: 1;
				padding: .75em;
				background-color: color-mix(in oklch, currentColor 10%, transparent);
				border-radius: .5rem;
			}

			.toast-actions {
				justify-self: center;
				align-self: center;
			}

			.toast-close {
				aspect-ratio: 1;
			}

			section {
				display: flex;
				align-items: center;
				flex-wrap: wrap;
				min-height: 2lh;
				width: 100%;
				gap: 1em;
			}

			.progress {
				display: block;
				position: absolute;
				bottom: 0;
				left: 0;
				height: 3px;
				width: 100%;
				opacity: .7;
				background-color: var(--_toast-color);
				transform-origin: left;

				&.active {
					animation: progress-shrink linear forwards;
				}
			}

			@keyframes progress-shrink {
				from { 
					transform: scaleX(1);
				}
				to { 
					transform: scaleX(0);
				}
			}
		}
	`,
	imports: [NgTemplateOutlet, NgComponentOutlet, ToastComponent, ButtonComponent, IconComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastPopoverComponent implements BasePopoverComponent {
	readonly #stackService = inject(NotificationStackService);

	readonly popoverRef = viewChild.required<ElementRef<unknown>>('toast');

	readonly closeButton = computed(() => this.options().closeButton ?? false);
	readonly toastType = computed(() => this.options().type ?? 'primary');
	readonly canClose = computed(() => this.options().canClose?.() ?? true);
	readonly disableClose = computed(() => !this.canClose());
	readonly actions = computed(() => this.options().actions ?? []);
	readonly duration = computed(() => this.options().duration ?? 0);
	readonly showProgress = computed(() => this.duration() > 0 && this.duration() !== Infinity && this.canClose());
	readonly pauseAutoCloseOnHover = computed(() => this.options().pauseAutoCloseOnHover ?? true);
	readonly icon = computed(() => {
		const data = this.options().data;
		return this.isTemplateData(data) ? null : data.icon ?? this.#iconFromType(this.options().type);
	});
	readonly title = computed(() => {
		const data = this.options().data;
		return this.isTemplateData(data) ? null : data.title ?? null;
	});
	readonly idName = computed(() => `toast-${this.options()._id}` as const);

	readonly options = input.required<ToastConfig & { _id: NotificationStackOptions['_id'] }>();
	#timeout?: ReturnType<typeof setTimeout>;
	#startTime: number = 0;
	#remaining: number = 0;

	readonly isPaused = signal(false);

	constructor() {
		effect(() => {
			const duration = this.options().duration;
			const canClose = untracked(() => this.canClose());
			if (duration && duration !== Infinity && canClose) {
				untracked(() => {
					this.#remaining = duration;
					this.#resume();
				});
			}
		});

		inject(DestroyRef).onDestroy(() => {
			clearTimeout(this.#timeout);
		});
	}

	public close() {
		const canClose = untracked(() => this.canClose());
		if (!canClose) return;

		this.#stackService.close(this.options()._id);
	}

	#resume() {
		if (this.#remaining <= 0) return;

		clearTimeout(this.#timeout);
		this.#startTime = Date.now();
		this.#timeout = setTimeout(() => this.close(), this.#remaining);
	}

	#iconFromType(type: ToastConfig['type']): IconName | null {
		return match(type)
			.with('error', () => 'cross-circle' as const)
			.with('warning', () => 'exclamation-triangle' as const)
			.with('info', () => 'info' as const)
			.with('success', () => 'check-circle' as const)
			.with('pending', () => 'spinner-1' as const)
			.otherwise(() => null);
	}

	protected getActionTypeName(action: ActionsButton) {
		return action.isPrimary ? 'transparent-primary' as const : 'transparent' as const;
	}

	protected handleMouseEnter() {
		if (!this.showProgress() || !this.pauseAutoCloseOnHover()) return;

		this.isPaused.set(true);
		clearTimeout(this.#timeout);
		this.#remaining -= Date.now() - this.#startTime;
	}

	protected handleMouseLeave() {
		if (!this.showProgress() || !this.pauseAutoCloseOnHover()) return;

		this.isPaused.set(false);
		this.#resume();
	}

	protected isComponentData(
		data: BaseNotificationItemConfig['data'],
	): data is BaseNotificationComponentDataConfig {
		return 'content' in data && typeof data.content === 'function';
	}

	protected isTemplateData(
		data: BaseNotificationItemConfig['data'],
	): data is BaseNotificationTemplateDataConfig {
		return 'content' in data && data.content instanceof TemplateRef;
	}
}
